// src/components/admin/UsuariosTab.jsx
import { useState, useMemo, useCallback, useEffect } from 'react';
import UsuarioForm from './UsuarioForm';
import UsuarioTable from './UsuarioTable';
import Paginacion from '../shared/Paginacion';

const USUARIOS_POR_PAGINA = 5;

export default function UsuariosTab({
  usuarios = [],
  onCrearUsuario,
  msg,
  onDarBaja,
  onReactivar,
  onEditar,
}) {
  const [filtro, setFiltro] = useState('');
  const [rolFiltro, setRolFiltro] = useState('todos');
  const [estadoFiltro, setEstadoFiltro] = useState('activos');
  const [pagina, setPagina] = useState(1);

  // No mutamos props:
  const lista = Array.isArray(usuarios) ? usuarios : [];

  const limpiar = useCallback(() => {
    setFiltro('');
    setRolFiltro('todos');
    setEstadoFiltro('activos');
    setPagina(1);
  }, []);

  // ESC para limpiar filtros (comodidad)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && (filtro || rolFiltro !== 'todos' || estadoFiltro !== 'activos')) limpiar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtro, rolFiltro, estadoFiltro, limpiar]);

  const filtrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    const byText = (u) => (u?.email || '').toLowerCase().includes(q);
    const byRole = (u) => rolFiltro === 'todos' || (u?.role || '') === rolFiltro;
    const byEstado = (u) => {
      const activo = u?.activo !== false;
      if (estadoFiltro === 'activos') return activo;
      if (estadoFiltro === 'baja') return !activo;
      return true;
    };
    return lista.filter((u) => byText(u) && byRole(u) && byEstado(u));
  }, [lista, filtro, rolFiltro, estadoFiltro]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / USUARIOS_POR_PAGINA));
  const visibles = useMemo(() => {
    const start = (pagina - 1) * USUARIOS_POR_PAGINA;
    return filtrados.slice(start, start + USUARIOS_POR_PAGINA);
  }, [filtrados, pagina]);

  const hasFilters = !!filtro || rolFiltro !== 'todos' || estadoFiltro !== 'activos';

  return (
    <div className="space-y-8">
      {/* Alta rápida */}
      <UsuarioForm onCrearUsuario={onCrearUsuario} msg={msg} />

      {/* Toolbar filtros */}
      <div className="rounded-2xl border border-white/60 bg-white/50 backdrop-blur px-3 sm:px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg sm:text-xl font-bold text-blue-900">
            👥 Listado de usuarios
          </h2>
          <p className="text-xs sm:text-sm text-gray-600">
            {filtrados.length} resultado{filtrados.length === 1 ? '' : 's'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          {/* Buscador */}
          <div className="relative w-full sm:w-64">
            <span
              className="absolute left-3 top-1/2 -translate-y-1/2 select-none"
              aria-hidden
            >
              🔍
            </span>
            <input
              type="text"
              placeholder="Buscar por email…"
              aria-label="Buscar por email"
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setPagina(1);
              }}
              className="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-300 bg-white/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {filtro && (
              <button
                onClick={() => {
                  setFiltro('');
                  setPagina(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>

          {/* Rol */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label
              htmlFor="filtro-rol"
              className="text-sm text-gray-700 whitespace-nowrap"
            >
              Rol
            </label>
            <select
              id="filtro-rol"
              value={rolFiltro}
              onChange={(e) => {
                setRolFiltro(e.target.value);
                setPagina(1);
              }}
              className="w-full sm:w-48 px-4 py-2 rounded-xl border border-gray-300 bg-white/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Todos</option>
              <option value="admin">Administrador</option>
              <option value="employee">Empleado</option>
            </select>
          </div>

          {/* Estado */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label
              htmlFor="filtro-estado"
              className="text-sm text-gray-700 whitespace-nowrap"
            >
              Estado
            </label>
            <select
              id="filtro-estado"
              value={estadoFiltro}
              onChange={(e) => {
                setEstadoFiltro(e.target.value);
                setPagina(1);
              }}
              className="w-full sm:w-48 px-4 py-2 rounded-xl border border-gray-300 bg-white/80 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="activos">Activos</option>
              <option value="baja">Bajas</option>
              <option value="todos">Todos</option>
            </select>
          </div>

          {/* Limpiar */}
          <div className="flex-1 sm:flex-none">
            <button
              onClick={limpiar}
              disabled={!hasFilters}
              className={`w-full sm:w-auto px-4 py-2 rounded-xl border transition ${
                hasFilters
                  ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                  : 'bg-white/60 text-gray-400 border-gray-200 cursor-not-allowed'
              }`}
              title="Quitar filtros"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {visibles.length > 0 ? (
        <>
          <UsuarioTable
            usuarios={visibles}
            onDarBaja={onDarBaja}
            onReactivar={onReactivar}
            onEditar={onEditar}
          />

          {totalPaginas > 1 && (
            <div className="mt-4">
              <Paginacion
                pagina={pagina}
                totalPaginas={totalPaginas}
                onAnterior={() => setPagina((p) => Math.max(1, p - 1))}
                onSiguiente={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                onGoToPage={(n) => setPagina(n)}
              />
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500 italic text-center">
          No se encontraron usuarios con ese filtro.
        </p>
      )}
    </div>
  );
}
